// CYBORG-549: where a fresh signup lands right after register/verify.
//
// Two cases:
//  - Came from an invite link (?next=/invite/<token>) — resume that flow
//    unconditionally, same as before. It accepts the invite itself and lands
//    the user in the workspace.
//  - Cold signup, no ?next. The server (routes/auth.ts provisionPasswordlessUser)
//    only auto-creates a personal "owner" workspace when the account has NO
//    activated invite — an invited email that signs up directly (without
//    clicking the invite link) instead gets ONLY the team workspace(s) it was
//    invited to. So "no owner-role workspace in the list" means this signup
//    was joined into a team, not started fresh: land in that workspace
//    directly (same destination accept-invite uses) instead of running the
//    create-your-own-workspace wizard against a workspace that was never
//    created. A normal fresh signup always gets exactly one owner workspace,
//    so it's unaffected and still goes to the onboarding wizard.
//
// CYBORG-555 gap this function CANNOT close by itself: an invite can carry
// role "owner" too (MCP/CLI only — no UI invite dialog offers it). A cold
// signup that activates ONE such invite comes back from the server shaped
// EXACTLY like a normal fresh signup — one workspace, role "owner" — so
// `hasOwnWorkspace` reads true and this still sends them to /onboarding,
// which would then rename/reconfigure the invited team. The auth response
// never carries the workspace's real creator (ownerId), so this function has
// no way to tell the two apart from role/id/name alone; closing that fully
// needs a server-side signal (routes/auth.ts / relay-standalone.ts — owned by
// other work, not touched here). Onboarding's own runCreation guard (CYBORG-555)
// covers the gap instead, using a signal it CAN observe (workspace roster) and
// reusing `joinedWorkspaceLandingPath` below for the same destination.
// Follow-up on file: reject owner-role invitations at the relay (the invite
// UI never offers "owner", so a relay-side reject would close the hole at
// the source instead of mitigating it downstream).
// A new account was never sent to a workspace, so a signup's `?next=/workspace/...` is a
// leftover of an earlier session on this browser (loginRedirectPath). Invites still win.
function signupNextDest(nextDest: string | null): string | null {
  if (nextDest && /^\/workspace(?:[/?#]|$)/.test(nextDest)) return null; // null: fresh-signup routing
  return nextDest;
}

export function postSignupLandingPath(opts: {
  nextDest: string | null;
  // `role: null` = a Cyborg7 Connect EXTERNAL (no membership). It is never "owner",
  // so it never counts as a workspace of your own — which is exactly right.
  workspaces: Array<{ id: string; role: string | null }>;
  isDesktop: boolean;
  /** Token of an invite still waiting for this brand-new account (it signed up without
   *  the email link). Resumes the same /invite/<token> the link would, never the wizard. */
  pendingInviteToken?: string | null;
}): string {
  const { workspaces, pendingInviteToken } = opts;
  const nextDest = signupNextDest(opts.nextDest);
  if (nextDest) return nextDest;

  const hasOwnWorkspace = workspaces.some((w) => w.role === "owner");
  if (!hasOwnWorkspace && pendingInviteToken) {
    return `/invite/${encodeURIComponent(pendingInviteToken)}`;
  }
  // Joined a team rather than made one: that workspace, where their own #onboarding opens.
  if (!hasOwnWorkspace && workspaces.length > 0) {
    return `/workspace/${workspaces[0].id}`;
  }

  return "/onboarding";
}

// The minimal public workspace shape the "Your team is already here" screen
// needs. Mirrors the server's `pendingDomainJoin` hint on the register/verify
// response (client.ts PendingDomainJoin), narrowed to what routing reads.
export interface PendingDomainMatch {
  workspaceId: string;
  name: string;
  avatarUrl: string | null;
  memberCount: number;
  members: { name: string; avatarUrl: string | null }[];
  /** How @domain signups join — drives "Join" (open) vs "Request to join". */
  joinPolicy: "request" | "open";
  domain: string;
}

// Corporate-email domain onboarding (CYBORG-onboarding): where a brand-new
// signup goes after OTP verify. Two outcomes:
//  - `{ kind: "domain-match" }` — the signup's email domain already belongs to a
//    registered team (the server sent `pendingDomainJoin` and created NO personal
//    workspace). The login page renders TeamFoundCard IN PLACE instead of
//    navigating, so the user can request-to-join or fall back to creating their
//    own. An explicit invite-link resume (`?next=/invite/…`) OUTRANKS a domain
//    match — an invite was a deliberate act, so it wins.
//  - `{ kind: "path" }` — no domain match: fall through to the existing
//    postSignupLandingPath routing (invite resume / joined team / onboarding).
//
// Pulled out as a pure function (not inlined in +page.svelte) so the priority
// rule is unit-testable without the OTP/RPC round-trip it's fed from.
export type PostSignupOutcome =
  | { kind: "domain-match"; match: PendingDomainMatch }
  | { kind: "path"; path: string };

export function resolvePostSignup(opts: {
  nextDest: string | null;
  pendingDomainJoin: PendingDomainMatch | null | undefined;
  // `role: null` = a Cyborg7 Connect EXTERNAL (no membership). It is never "owner",
  // so it never counts as a workspace of your own — which is exactly right.
  workspaces: Array<{ id: string; role: string | null }>;
  isDesktop: boolean;
  pendingInviteToken?: string | null;
}): PostSignupOutcome {
  const { pendingDomainJoin, workspaces, isDesktop, pendingInviteToken } = opts;
  const nextDest = signupNextDest(opts.nextDest);
  // An invite-link resume is an explicit, user-initiated destination — never
  // override it with a passive domain match.
  if (!nextDest && pendingDomainJoin) {
    return { kind: "domain-match", match: pendingDomainJoin };
  }
  return {
    kind: "path",
    path: postSignupLandingPath({ nextDest, workspaces, isDesktop, pendingInviteToken }),
  };
}

// Where a user who JOINED a workspace (rather than created one) lands: same
// destination whether they were invited as member/admin/viewer (above) or,
// per CYBORG-555, as owner and caught by onboarding's own guard instead.
// Exported so both call sites produce byte-identical routing.
export function joinedWorkspaceLandingPath(workspaceId: string, isDesktop: boolean): string {
  return isDesktop
    ? `/workspace/${workspaceId}`
    : `/welcome/download?next=${encodeURIComponent(`/workspace/${workspaceId}`)}`;
}

// CYBORG-555: the signal onboarding's runCreation guard uses to tell an
// owner-role INVITE apart from a genuinely fresh signup's auto-created
// workspace, since role/id/name alone can't (see the note above). A workspace
// this signup just auto-created has nobody in it but the signee — nothing
// else could have joined in the seconds since registration. A workspace this
// account was invited into (as owner or otherwise) already has its real
// owner/teammates as members. So "does the roster contain anyone but me"
// reliably tells them apart without a new server field. Pulled out as a pure
// function (not left inline in +page.svelte) so it's unit-testable without
// mocking the roster RPC it's fed from.
export function workspaceHasOtherMembers(
  members: Array<{ userId: string }>,
  myUserId: string | null | undefined,
): boolean {
  return members.some((m) => m.userId !== myUserId);
}
